import { LogOutIcon, ShieldIcon, UserRoundIcon } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Link, useLocation } from "react-router-dom";

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Separator } from "@/components/ui/separator";
import { SidebarTrigger } from "@/components/ui/sidebar";

import { ChangeLanguage, ChangeTheme, RoleBadge } from "@/components/shared";

import { resolveMediaUrl } from "@/lib/media";
import { useLogoutAlert } from "@/hooks";

import { PAGE_PATHS } from "@/constants";
import { useAuthStore, useRole } from "@/store";

import { findNavLabelKey, findNavSectionKey } from "../nav-config";
import { NotificationBell } from "./notification-bell";

/** "Aziz Karimov" → "AK". Rasm bo'lmasa shu ko'rinadi. */
const initialsOf = (first?: string, last?: string): string =>
  `${first?.[0] ?? ""}${last?.[0] ?? ""}`.toUpperCase() || "?";

export function Header() {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const user = useAuthStore(state => state.data?.user);
  const role = useRole();
  const openLogout = useLogoutAlert(store => store.onOpen);

  const sectionKey = findNavSectionKey(pathname);
  const pageKey = findNavLabelKey(pathname);

  return (
    <header className="border-border bg-background/85 sticky top-0 z-30 flex h-14 shrink-0 items-center gap-2 border-b px-3 backdrop-blur-md sm:px-4">
      <SidebarTrigger />
      <Separator orientation="vertical" className="mr-1 h-5" />

      {/* Breadcrumb kichik ekranda yashiriladi: u yerda sarlavha uchun joy
          yo'q va sahifa nomi kontentning o'zida ham bor. */}
      <Breadcrumb className="hidden min-w-0 sm:block">
        <BreadcrumbList>
          <BreadcrumbItem>
            <BreadcrumbLink asChild>
              <Link to={PAGE_PATHS.DASHBOARD}>{t("NAV.DASHBOARD")}</Link>
            </BreadcrumbLink>
          </BreadcrumbItem>
          {sectionKey && sectionKey !== "NAV.SECTION_GENERAL" && (
            <>
              <BreadcrumbSeparator />
              <BreadcrumbItem>{t(sectionKey)}</BreadcrumbItem>
            </>
          )}
          {pageKey && pageKey !== "NAV.DASHBOARD" && (
            <>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbPage className="truncate">{t(pageKey)}</BreadcrumbPage>
              </BreadcrumbItem>
            </>
          )}
        </BreadcrumbList>
      </Breadcrumb>

      <div className="ml-auto flex items-center gap-1">
        <ChangeLanguage className="px-2" showIcon />
        <ChangeTheme />
        <NotificationBell />

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="rounded-full"
              aria-label={t("COMMON.ACCOUNT_MENU")}
            >
              <Avatar className="size-8">
                {user?.avatarUrl && <AvatarImage src={resolveMediaUrl(user.avatarUrl)} alt="" />}
                <AvatarFallback className="text-xs">
                  {initialsOf(user?.firstName, user?.lastName)}
                </AvatarFallback>
              </Avatar>
            </Button>
          </DropdownMenuTrigger>

          <DropdownMenuContent align="end" className="w-60">
            <DropdownMenuLabel className="space-y-1">
              <p className="truncate font-medium">
                {[user?.firstName, user?.lastName].filter(Boolean).join(" ") || t("COMMON.USER")}
              </p>
              {user?.username && (
                <p className="text-primary truncate text-xs font-medium">@{user.username}</p>
              )}
              <p className="text-muted-foreground truncate text-xs font-normal">{user?.email}</p>
              {/* Rol ko'rinib turadi: odam nima uchun ba'zi bo'limlarni
                  ko'rmayotganini o'zi tushunadi. */}
              <RoleBadge role={role} />
            </DropdownMenuLabel>
            <DropdownMenuSeparator />

            <DropdownMenuItem asChild>
              <Link to={PAGE_PATHS.PROFILE}>
                <UserRoundIcon className="size-4" />
                {t("NAV.PROFILE")}
              </Link>
            </DropdownMenuItem>
            <DropdownMenuItem asChild>
              <Link to={PAGE_PATHS.SECURITY}>
                <ShieldIcon className="size-4" />
                {t("NAV.SECURITY")}
              </Link>
            </DropdownMenuItem>

            <DropdownMenuSeparator />

            {/* Chiqish qaytarib bo'ladi, lekin ishni to'xtatadi: bir bosishda
                ochiq forma ham, filtrlar ham yo'qoladi — shuning uchun
                tasdiqlash oynasi orqali. */}
            <DropdownMenuItem
              variant="destructive"
              onSelect={event => {
                event.preventDefault();
                openLogout();
              }}
            >
              <LogOutIcon className="size-4" />
              {t("AUTH.LOGOUT")}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
}
